import { useEffect } from 'react'
import { Box, Input, Radio, RadioGroup, Text, Title } from '@holbonkit/ui'
import { Controller, useForm } from 'react-hook-form'

import { sanitizeString } from '../../lib/capitalize-first-letter'
import type { TStepFormProps } from '../../lib/step-form-props'
import type { TCitizenship } from '../../model/types'

import { AddressBottomSheet } from './address-bottom-sheet'
import { SearchBottomSheet } from './search-bottom-sheet'
import type { TAddressFormNotRF, TAddressFormRF } from './types'
import { addressValidator } from './validator'

import styles from './address.module.scss'

export const ADDRESS_FORM_ID = 'smz-address-form'

const EMPTY_ADDRESS_RF: TAddressFormRF = {
    address: '',
    isTaxResident: '',
}

const EMPTY_COUNTRY = { code: '', name: '' }

const EMPTY_ADDRESS_NOT_RF: TAddressFormNotRF = {
    birthCountry: EMPTY_COUNTRY,
    citizenship: EMPTY_COUNTRY,
    placeOfBirth: '',
    residenceCountry: EMPTY_COUNTRY,
}

type TAddressProps = TStepFormProps<TAddressFormNotRF | TAddressFormRF> & {
    citizenship: TCitizenship
}

export const Address = ({
    citizenship,
    defaultValues,
    formId,
    onSubmit,
    onValidityChange,
}: TAddressProps) => {
    const {
        clearErrors,
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm<TAddressFormNotRF | TAddressFormRF>({
        defaultValues: citizenship === 'rf' ? EMPTY_ADDRESS_RF : EMPTY_ADDRESS_NOT_RF,
        mode: 'onTouched',
        reValidateMode: 'onChange',
    })

    useEffect(() => {
        reset(defaultValues ?? (citizenship === 'rf' ? EMPTY_ADDRESS_RF : EMPTY_ADDRESS_NOT_RF))
    }, [defaultValues, reset, citizenship])

    useEffect(() => {
        onValidityChange?.(isValid)
    }, [isValid, onValidityChange])

    return (
        <form noValidate id={formId} onSubmit={handleSubmit(onSubmit)}>
            <Box className={styles['address']} pb={4} pt={6} px={4}>
                <Title variant='header-24-bold'>
                    {citizenship === 'not_rf' ? 'Страна и место рождения' : 'Адрес'}
                </Title>
                {citizenship === 'rf' && (
                    <>
                        <Controller
                            control={control}
                            name='address'
                            rules={addressValidator.address}
                            render={({ field, fieldState }) => (
                                <Input
                                    errorText={fieldState.error?.message}
                                    label='Адрес регистрации как в паспорте'
                                    maxLength={250}
                                    size='lg'
                                    type='text'
                                    {...field}
                                    onChange={(event) => {
                                        field.onChange(sanitizeString(event.target.value))
                                    }}
                                    onFocus={() => {
                                        clearErrors('address')
                                    }}
                                />
                            )}
                        />
                        <Box mt={2}>
                            <Title variant='header-24-bold'>
                                Вы являетесь налоговым <br /> резидентом РФ?
                            </Title>
                        </Box>
                        <Controller
                            control={control}
                            name='isTaxResident'
                            rules={addressValidator.isTaxResident}
                            render={({ field, fieldState }) => (
                                <Box className={styles['radio-group-field']}>
                                    <RadioGroup
                                        className={styles['radio-group']}
                                        invalid={Boolean(fieldState.error)}
                                        value={field.value}
                                        onValueChange={(value) => {
                                            field.onChange(value)
                                            clearErrors('isTaxResident')
                                        }}
                                    >
                                        <Radio
                                            className={styles['radio-group__item']}
                                            size='md'
                                            value='yes'
                                        >
                                            Да
                                        </Radio>
                                        <Radio
                                            className={styles['radio-group__item']}
                                            size='md'
                                            value='no'
                                        >
                                            Нет
                                        </Radio>
                                    </RadioGroup>
                                    {fieldState.error?.message ?
                                        <Text
                                            as='p'
                                            className={styles['radio-group-field__error']}
                                            variant='body-s-14-regular'
                                        >
                                            {fieldState.error.message}
                                        </Text>
                                    :   null}
                                </Box>
                            )}
                        />
                    </>
                )}
                {citizenship === 'not_rf' && (
                    <>
                        <Controller
                            control={control}
                            name='citizenship'
                            rules={addressValidator.citizenship}
                            render={({ field, fieldState }) => (
                                <SearchBottomSheet
                                    errorClassName={styles['residence-country-error']}
                                    errorText={fieldState.error?.message}
                                    label='Гражданство'
                                    name={field.name}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='birthCountry'
                            rules={addressValidator.birthCountry}
                            render={({ field, fieldState }) => (
                                <SearchBottomSheet
                                    errorClassName={styles['residence-country-error']}
                                    errorText={fieldState.error?.message}
                                    label='Страна рождения'
                                    name={field.name}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='placeOfBirth'
                            rules={addressValidator.placeOfBirth}
                            render={({ field, fieldState }) => (
                                <Input
                                    errorText={fieldState.error?.message}
                                    label='Место рождения как в документе'
                                    size='lg'
                                    type='text'
                                    {...field}
                                    onChange={(event) => {
                                        field.onChange(sanitizeString(event.target.value))
                                    }}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name='residenceCountry'
                            rules={addressValidator.residenceCountry}
                            render={({ field, fieldState }) => (
                                <SearchBottomSheet
                                    errorClassName={styles['residence-country-error']}
                                    errorText={fieldState.error?.message}
                                    label='Страна налогового резидентства'
                                    name={field.name}
                                    value={field.value}
                                    onBlur={field.onBlur}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </>
                )}
                <AddressBottomSheet />
            </Box>
        </form>
    )
}
